import { Plus, Info } from 'lucide-react';
import { ObjectionCard } from './ObjectionCard';
import type { ObjectionItem } from '../../types/settings';
import type { TemplateVariable } from '../../hooks/useVariableInsertion';

interface ObjectionHandlingSectionProps {
  objections: ObjectionItem[];
  onUpdateObjection: (id: string, updates: Partial<ObjectionItem>) => void;
  onDeleteObjection: (id: string) => void;
  onAddObjection: () => void;
  onGenerateAI?: (objectionId: string, tone: string, length: string, currentContent?: string) => Promise<string>;
  onCardDirtyChange?: (cardKey: string, isDirty: boolean) => void;
  variables?: TemplateVariable[];
}

export function ObjectionHandlingSection({
  objections,
  onUpdateObjection,
  onDeleteObjection,
  onAddObjection,
  onGenerateAI,
  onCardDirtyChange,
  variables,
}: ObjectionHandlingSectionProps) {
  const configuredCount = objections.filter(obj => obj.response).length;
  const totalCount = objections.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-extrabold text-gray-900 font-display">
            Common Objections & Responses
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            These objection handlers apply to both First Call and Follow-up Call scripts
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm font-medium text-gray-700">
              {configuredCount} of {totalCount} configured
            </span>
            <div className="flex-1 max-w-xs h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#1875FF] to-[#05E0D1] transition-all duration-300"
                style={{ width: `${(configuredCount / totalCount) * 100}%` }}
              />
            </div>
          </div>
        </div>
        <button
          onClick={onAddObjection}
          className="px-4 py-2.5 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 font-semibold"
        >
          <Plus className="w-5 h-5" />
          Add Objection
        </button>
      </div>

      <div className="p-4 bg-[#1875FF]/5 rounded-lg border border-[#1875FF]/20">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-[#1875FF] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-900 mb-1">Objection Handling Best Practices</p>
            <p className="text-xs text-gray-700">
              Acknowledge the objection, provide value, and guide the conversation forward. Use the "Add Variables" button inside each card to personalize responses.
            </p>
          </div>
        </div>
      </div>

      {objections.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <p className="text-gray-500 mb-4">No objections configured yet</p>
          <button
            onClick={onAddObjection}
            className="px-6 py-3 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 font-semibold mx-auto"
          >
            <Plus className="w-5 h-5" />
            Add Your First Objection
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {objections
            .sort((a, b) => a.order - b.order)
            .map((objection) => (
              <ObjectionCard
                key={objection.id}
                title={objection.title}
                response={objection.response}
                category={objection.category}
                onUpdateTitle={(title) => onUpdateObjection(objection.id, { title })}
                onUpdateResponse={(response) => onUpdateObjection(objection.id, { response })}
                onUpdateCategory={(category) => onUpdateObjection(objection.id, { category })}
                onDelete={() => onDeleteObjection(objection.id)}
                onGenerateAI={
                  onGenerateAI
                    ? (tone, length, currentContent) => onGenerateAI(objection.id, tone, length, currentContent)
                    : undefined
                }
                onDirtyChange={
                  onCardDirtyChange
                    ? (isDirty) => onCardDirtyChange(objection.id, isDirty)
                    : undefined
                }
                variables={variables}
              />
            ))}
        </div>
      )}
    </div>
  );
}
